// Appointments with a time: the vet at 10:40, puppy class at 18:30.
// Pure; used by Today, Health, the brief, phone alerts and the calendar.

import { MINUTE, addDays, formatTime, today, toISODate } from './time.ts';
import type { Appointment, AppointmentKind, Contact, Millis } from './types.ts';

export const APPOINTMENT_KINDS: { kind: AppointmentKind; label: string; minutes: number }[] = [
  { kind: 'vet', label: 'Vet', minutes: 20 },
  { kind: 'vaccination', label: 'Vaccination', minutes: 20 },
  { kind: 'class', label: 'Puppy class', minutes: 60 },
  { kind: 'groomer', label: 'Groomer', minutes: 90 },
  { kind: 'other', label: 'Other', minutes: 30 },
];

export const kindLabel = (k: AppointmentKind) => APPOINTMENT_KINDS.find((x) => x.kind === k)?.label ?? 'Appointment';

export const apptMinutes = (a: Appointment) => a.minutes ?? APPOINTMENT_KINDS.find((x) => x.kind === a.kind)?.minutes ?? 30;
export const apptEnd = (a: Appointment) => a.at + apptMinutes(a) * MINUTE;

/** Vet-type appointments get written up afterwards. */
export const isVetKind = (a: Appointment) => a.kind === 'vet' || a.kind === 'vaccination';

/** Where, as words: the contact's name, else the free text. */
export function apptPlace(a: Appointment, contacts: Contact[] = []): string | undefined {
  return contacts.find((c) => c.id === a.contactId)?.name ?? (a.place?.trim() || undefined);
}

/** Booked and not yet over (or still happening), soonest first. */
export function upcomingAppointments(appts: Appointment[], now: Millis): Appointment[] {
  return appts.filter((a) => !a.cancelled && apptEnd(a) > now).sort((a, b) => a.at - b.at);
}

/** Vet appointments that have finished in the last 3 days with nothing written down. */
export function needsWriteUp(appts: Appointment[], now: Millis): Appointment[] {
  return appts.filter((a) => !a.cancelled && isVetKind(a) && !a.visitId && !a.noWriteUp && apptEnd(a) <= now && now - apptEnd(a) < 3 * 24 * 60 * MINUTE).sort((a, b) => b.at - a.at);
}

/** "today 10:40", "tomorrow 18:30", "Tue 23 Oct 10:40". */
export function whenLabel(at: Millis, now: Millis): string {
  const d = toISODate(at);
  const t = formatTime(at);
  if (d === today(now)) return `today ${t}`;
  if (d === addDays(today(now), 1)) return `tomorrow ${t}`;
  return `${new Date(at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${t}`;
}

/** "in 45 min", "in 3 h", or undefined when not within the day. */
export function countdown(at: Millis, now: Millis): string | undefined {
  const mins = Math.round((at - now) / MINUTE);
  if (mins < 0) return undefined;
  if (mins < 60) return `in ${mins} min`;
  if (mins < 12 * 60) return `in ${Math.floor(mins / 60)} h${mins % 60 >= 30 ? ' 30' : ''}`;
  return undefined;
}

/** One line: "Vet 10:40 · Queens Crescent Vets". */
export function apptLine(a: Appointment, contacts: Contact[] = []): string {
  const place = apptPlace(a, contacts);
  return `${a.title} ${formatTime(a.at)}${place ? ` · ${place}` : ''}`;
}
